
import React from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { Helmet } from "react-helmet";
import { Search, Home, Calculator, Heart, DollarSign, Briefcase, GraduationCap, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Enhanced 404 page with search suggestions and popular calculator links.
 */
const NotFound = () => {
  const popularCalculators = [
    { name: "BMI Calculator", path: "/calculators/health-wellness/bmi", icon: Heart },
    { name: "Mortgage Calculator", path: "/calculators/financial-tools/mortgage", icon: DollarSign },
    { name: "GPA Calculator", path: "/calculators/education/gpa", icon: GraduationCap },
    { name: "Age Calculator", path: "/calculators/lifestyle/age", icon: Sparkles },
    { name: "ROI Calculator", path: "/calculators/professional-tools/roi", icon: Briefcase },
    { name: "Loan Calculator", path: "/calculators/financial-tools/loan", icon: DollarSign },
  ];

  const categories = [
    { name: "Financial Tools", path: "/calculators/financial-tools", icon: DollarSign },
    { name: "Health & Wellness", path: "/calculators/health-wellness", icon: Heart },
    { name: "Education", path: "/calculators/education", icon: GraduationCap },
    { name: "Lifestyle", path: "/calculators/lifestyle", icon: Sparkles },
    { name: "Professional", path: "/calculators/professional-tools", icon: Briefcase },
  ];

  return (
    <Layout>
      <Helmet>
        <title>404 - Page Not Found | GetCalculator</title>
        <meta name="description" content="The page you're looking for doesn't exist. Browse our 55+ free calculators for finance, health, education, and more." />
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      <div className="container mx-auto px-4 py-16 max-w-4xl">
        {/* Hero section */}
        <div className="text-center mb-12 animate-fade-in">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-primary/10 mb-6">
            <Search className="h-10 w-10 text-primary" />
          </div>
          <h1 className="text-5xl font-bold mb-4 text-foreground">404</h1>
          <p className="text-xl text-muted-foreground mb-2">Page not found</p>
          <p className="text-muted-foreground max-w-md mx-auto">
            The page you're looking for doesn't exist or has been moved. Try one of our popular calculators below.
          </p>
          <Button asChild className="mt-6">
            <Link to="/">
              <Home className="mr-2 h-4 w-4" />
              Back to Home
            </Link>
          </Button>
        </div>

        {/* Popular calculators */}
        <div className="mb-12">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Calculator className="h-5 w-5 text-primary" />
            Popular Calculators
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {popularCalculators.map(calc => (
              <Link
                key={calc.path}
                to={calc.path}
                className="flex items-center gap-3 p-3 rounded-lg border border-border hover:border-primary hover:shadow-md transition-all duration-200 hover:-translate-y-0.5"
              >
                <div className="p-2 rounded-md bg-primary/10">
                  <calc.icon className="h-4 w-4 text-primary" />
                </div>
                <span className="font-medium text-sm">{calc.name}</span>
              </Link>
            ))}
          </div>
        </div>

        {/* Browse by category */}
        <div>
          <h2 className="text-lg font-semibold mb-4">Browse by Category</h2>
          <div className="flex flex-wrap gap-3">
            {categories.map(cat => (
              <Link
                key={cat.path}
                to={cat.path}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-border hover:border-primary hover:bg-primary/5 transition-all duration-200 text-sm font-medium"
              >
                <cat.icon className="h-4 w-4" />
                {cat.name}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default NotFound;
